import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AutomaticSavingsFeaturesEnabled,
  InlineAlertType,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import { UserInfo } from 'features/accounts/AccountDetails/AccountDetails.helpers';
import { useUpdateMYP } from 'features/believe/moveYourPay/hooks/useUpdateMyp';
import MoveYourPayDirectDepositBottomSheet from 'features/believe/moveYourPay/MoveYourPayDirectDepositBottomSheet/MoveYourPayDirectDepositBottomSheet';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetAutomaticChecking from 'services/graphql/queries/MoveMoney/GetAutomaticChecking';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import LinkButton from 'shared/components/vxl/LinkButton/LinkButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { useHandleKeypadButtonPress } from 'shared/hooks/useHandleKeypadButtonPress';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import {
  showZendeskArticle,
  ZendeskArticles,
} from 'shared/utils/zendesk/zendeskUtils';
import { useQuery } from 'urql';

const MoveYourPay = () => {
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'moveYourPay',
  });
  const { bottom } = useSafeAreaInsets();
  const directDepositSheetRef = useRef<BottomSheetModal>(null);
  const { isPinwheelLoading } = usePinwheel();
  const [isDirectDepositSheetOpen, setIsDirectDepositSheetOpen] =
    useState(false);
  const [shouldShowSheetAfterSnackbar, setShouldShowSheetAfterSnackbar] =
    useState<boolean>();
  const isChangesSavedSnackbarVisible =
    shouldShowSheetAfterSnackbar !== undefined;
  const [{ data, fetching }] = useQuery<UserInfo>({
    query: GetAutomaticChecking,
    requestPolicy: 'cache-and-network',
  });

  const automaticSavings = data?.user?.customerInfo
    ?.automaticSavings as AutomaticSavingsFeaturesEnabled;
  const sypPercentage = automaticSavings?.syp?.percentage as string;
  const existingPercentage = data?.user?.customerInfo?.myp
    ?.percentage as string;

  const wholeNumberPercentage = `${Math.round(parseFloat(existingPercentage)) || 0}%`;
  const existingMYPercentage = Math.round(parseFloat(sypPercentage)) || 0;

  const {
    updatedSYPPercentage: updatedMYPPercentage,
    handleKeypadButtonPress,
  } = useHandleKeypadButtonPress(wholeNumberPercentage);

  const isSubmitButtonDisabled =
    updatedMYPPercentage === wholeNumberPercentage ||
    parseFloat(updatedMYPPercentage) > 100 - existingMYPercentage;

  const { handleUpdateMYP, updateMYPFetching, isErrorShow, leaveMYPScreen } =
    useUpdateMYP(updatedMYPPercentage, setShouldShowSheetAfterSnackbar);

  const learnMorePressEvent = usePressableAnalytics({
    analyticsId: 'learnMore',
    customAttributes: {
      faq_name: 'move_your_pay',
      account_type: 'charge_card',
    },
  });

  const backButtonPressEvent = usePressableAnalytics({
    analyticsId: 'moveYourPayExited',
    customAttributes: {
      account_type: 'charge_card',
    },
  });

  const handleLearnMorePress = () => {
    learnMorePressEvent();
    showZendeskArticle(ZendeskArticles?.BELIEVE_MOVE_YOUR_PAYMENT);
  };
  const percentLimitMessage = translatedText('percentLimitMessage', {
    existingPercentage: 100 - existingMYPercentage,
  });

  const handleChangesSavedSnackbarHide = () => {
    setShouldShowSheetAfterSnackbar(undefined);
    if (shouldShowSheetAfterSnackbar) {
      setIsDirectDepositSheetOpen(true);
      directDepositSheetRef.current?.present();
    } else {
      leaveMYPScreen();
    }
  };

  const goBack = () => {
    if (isDirectDepositSheetOpen && isPinwheelLoading) {
      return;
    }
    if (isDirectDepositSheetOpen) {
      directDepositSheetRef.current?.dismiss();
      return;
    }
    backButtonPressEvent();
    navigateBack();
  };

  useBackHandler(goBack);

  return (
    <View style={[styles.screenContainer, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('headerTitle'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={goBack} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching && !data}
        LoadingComponent={
          <View>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={null}
      />
      <View style={styles.container}>
        <View>
          {isErrorShow ? (
            <View style={styles.inlineAlert}>
              <InlineAlert
                alertType={InlineAlertType.Error}
                bodyText={translatedText('errorMessage')}
                iconKeyType="ERROR"
              />
            </View>
          ) : null}
          <View style={styles.message}>
            <BodyBaseText>{translatedText('description')}</BodyBaseText>
            <LinkButton
              linkOnPress={() => handleLearnMorePress()}
              size="base"
              text={translatedText('linkText')}
              linkOnDark={false}
            />
          </View>
        </View>
        <View style={styles.percentageMainWrapper}>
          <ExpressiveXXLText style={styles.updatedMYPPercentage}>
            {updatedMYPPercentage}
          </ExpressiveXXLText>
        </View>
        <View>
          <BodyXSText
            style={
              parseFloat(updatedMYPPercentage) <= 100 - existingMYPercentage
                ? styles.percentLimitMessage
                : styles.percentLimitMessageError
            }
          >
            {percentLimitMessage}
          </BodyXSText>
        </View>
        <View style={styles.numPadContainer}>
          <NumberPad
            handleButtonPress={handleKeypadButtonPress}
            handleSubmit={handleUpdateMYP}
            submitButtonLabel={translatedText('updateButtonLabel')}
            submitDisabled={isSubmitButtonDisabled}
            submitButtonLoading={updateMYPFetching}
            disabled={
              updateMYPFetching ||
              isChangesSavedSnackbarVisible ||
              isDirectDepositSheetOpen
            }
          />
        </View>
      </View>
      <MoveYourPayDirectDepositBottomSheet
        bottomSheetRef={directDepositSheetRef}
        onFinish={leaveMYPScreen}
        setIsBottomSheetOpened={setIsDirectDepositSheetOpen}
      />
      {isChangesSavedSnackbarVisible ? (
        <Snackbar
          text={translatedText('changesSavedMessage')}
          onHide={handleChangesSavedSnackbarHide}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'space-between',
  },
  inlineAlert: {
    marginBottom: theme.spacing.l,
  },
  message: {
    paddingHorizontal: theme.spacing.m,
  },
  numPadContainer: {
    height: 328,
    paddingHorizontal: theme.spacing.m,
    width: '100%',
  },
  percentLimitMessage: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  percentLimitMessageError: {
    color: theme.color.status.negative,
    textAlign: 'center',
  },
  percentageMainWrapper: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
  },
  screenContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  updatedMYPPercentage: {
    textAlign: 'center',
  },
});

export default MoveYourPay;
